import RenderManager from "../rendering/render-manager";
import MaterialBuilder from "../rendering/material-builder";
import getPersistentUrl from "../utils/persistent-path";
import AssetLibrary from "../assets/asset-library";

const tmpMesh = new THREE.Mesh(undefined, new THREE.MeshBasicMaterial({ color: 0xff0000, side: THREE.DoubleSide }));
const raycaster = new THREE.Raycaster();
const originalPosition = new THREE.Vector3();
const axis = new THREE.Vector3(0, 1, 0);
const twoPI = 2 * Math.PI;
// const tmpQuat = new THREE.Quaternion();
const gltfLoader = new THREE.GLTFLoader();

class AssessmentMath {
    constructor({ tray, denominator, objects, position = new THREE.Vector3(), onChange, showVisuals = false }) {
        this._startThehta = 0;
        this._curThetha = 0;
        this._denominator = denominator;

        this.onChange = () => onChange && onChange();

        const { plops, geometryPlop } = createDropzones(12, position);

        const materialPlop = new THREE.LineBasicMaterial({ color: 0xff00ff });
        const edgesPlop = new THREE.EdgesGeometry(geometryPlop);

        if (showVisuals) {
            this._plopVisuals = new THREE.Group();

            plops.forEach(({ matrix }) => {
                const mesh = new THREE.LineSegments(edgesPlop, materialPlop);

                matrix.decompose(mesh.position, mesh.quaternion, mesh.scale);

                mesh.dontShowInHierarchyPanel = true;

                this._plopVisuals.add(mesh);
            });

            RenderManager.activeScene.add(this._plopVisuals);
        }

        this._objects = objects.slice();
        this._plops = plops;
        this._plopGeometry = geometryPlop;
        this._elements = [];

        this._root = new THREE.Group();
        this._root.position.copy(position);

        this._parent = new THREE.Group();
        this._parent.quaternion.setFromAxisAngle(new THREE.Vector3(1, 0, 0), Math.PI * -0.25);

        this._root.add(this._parent);
        RenderManager.activeScene.add(this._root)

        this._tray = null;
        this._isDisposed = false;
        tray.then(({ geometry, material }) => {
            if (this._isDisposed) return;
            this._tray = new THREE.Mesh(geometry, material);
            // this._tray.position.copy(position);
            this._parent.add(this._tray);
        });
    }

    getValue() {
        return this._elements.reduce((accum, { denominator }) => {
            const tN = accum.denominator, tD = denominator * accum.denominator;
            const oN = accum.numerator * denominator;

            const { numerator: n, denominator: d } = simplifyFraction(tN + oN, tD);

            accum.numerator = n;
            accum.denominator = d;

            return accum;
        }, { numerator: 0, denominator: 1 });
    }

    add(n1, d1, n2, d2) { return { numerator: n1 * d2 + n2 * d1, denominator: d1 * d2 }; }
    isOverflown({ numerator, denominator }) { return numerator > denominator; }

    get numerator() { return this.getValue().numerator; }
    get denominator() { return this.getValue().denominator; }

    plop(object, { matrix, thetha }) {

        if (!object.destructable) {
            const { numerator, denominator } = this.getValue();
            const isOverflown = this.isOverflown(this.add(1, object.denominator, numerator, denominator));

            if (!isOverflown) {

                const mesh = new THREE.Mesh(object.geometry, object.material);
                mesh.denominator = object.denominator;
                mesh.destructable = true;
                mesh.assessment = this;

                this._objects.push(mesh);

                this._parent.add(mesh);

                const denominatorThetha = twoPI / object.denominator;

                if (this._elements.length === 0) {
                    // matrix.decompose(mesh.position, mesh.quaternion, mesh.scale);
                    mesh.quaternion.setFromAxisAngle(axis, thetha);
                    this._startThehta = thetha;
                    this._curThetha = thetha + denominatorThetha;
                    mesh.thetha = thetha;
                    // mesh.quaternion.setFromAxisAngle(axis, this._startThetha);
                } else {
                    // matrix.decompose(mesh.position, tmpQuat, mesh.scale);
                    mesh.quaternion.setFromAxisAngle(axis, this._curThetha);
                    mesh.thetha = this._curThetha;
                    this._curThetha += denominatorThetha;
                }

                // mesh.rotateX(Math.PI * -0.25);

                this._elements.push(mesh);
            }
        }
        this.onChange();
    }

    cleanup() {
        let el = null;

        while (el = this._elements.pop()) {
            el.parent.remove(el);

            const idx = this._objects.indexOf(el);
            if (idx >= 1) {
                this._objects.splice(idx, 1);
            }
        }
        this.onChange();
    }

    destroy() {
        this.cleanup();

        if (this._plopVisuals) this._plopVisuals.remove(this._plopVisuals);
        if (this._tray) this._tray.parent.remove(this._tray);

        this._root.parent.remove(this._root);

        this._isDisposed = true;
        this._tray = null;
    }

    check(mouse, object) {
        if (object.destructable && object.assessment !== this) return;

        tmpMesh.geometry = this._plopGeometry;

        raycaster.setFromCamera(mouse, RenderManager.activeCamera);

        const intersections = [];

        this._plops.forEach(({ matrix, thetha }) => {
            const itscts = [];
            matrix.decompose(tmpMesh.position, tmpMesh.quaternion, tmpMesh.scale);
            // tmpMesh.quaternion.premultiply(quaternionRotation);
            tmpMesh.updateMatrixWorld();

            tmpMesh.raycast(raycaster, itscts);

            const itsct = itscts[0] || null;

            if (!itsct) return;

            itsct.matrix = matrix;
            itsct.thetha = thetha;

            intersections.push(itsct);
        });

        const intersection = intersections.sort((a, b) => a.distance - b.distance)[0] || null;

        if (!intersection) {
            if (object.destructable) {
                object.parent.remove(object);
                let idx = this._elements.indexOf(object);

                if (idx >= 0) {
                    this._elements.splice(idx, 1);
                    idx = this._objects.indexOf(object);
                    if (idx >= 0) {
                        this._objects.splice(idx, 1);

                        this._startThehta = this._elements.length > 0 ? this._elements[0].thetha : 0;
                        this._curThetha = this._startThehta;
                        this._elements.forEach(object => {
                            const denominatorThetha = twoPI / object.denominator;

                            object.quaternion.setFromAxisAngle(axis, this._curThetha);

                            this._curThetha += denominatorThetha;
                        });
                    }
                }
            }
            this.onChange();
            return false;
        };

        this.plop(object, intersection)

        return true;
    }

    //pickup(object) { originalPosition.copy(object.position); }
    //release(object) { object.position.copy(originalPosition); }
    pickup(object) {
        object.material.depthTest = false;
        object.material.transparent = true;
        originalPosition.copy(object.position);
    }
    release(object) {
        object.material.depthTest = true;
        object.material.transparent = false;
        object.position.copy(originalPosition);
    }

    get object() { return this._objects; }

    update() { }

    addFraction(denominator) {
        const index = Math.max(0, Math.min(denominator - 1, this._denominator));
        const object = this._objects[index];

        this.plop(object, this._plops[0]);
    }
}

const createDroppableObjects = (function () {
    const tmpGeometry = new THREE.BufferGeometry();
    tmpGeometry.addAttribute("position", new THREE.Float32BufferAttribute([], 3));



    const geometries = {
        pizza: [],
        basic: [],
        cake: []
    };

    return function (denominator, geometryType, cards = true) {
        const urlBase = getPersistentUrl();
        // const pizzaPie = ["pizza/Pizza_1.gltf", "pizza/Pizza_1_2.gltf", "pizza/Pizza_1_3.gltf", "pizza/Pizza_1_4.gltf"];
        const pizzaPie = "pizza/Pizza_COMPLETE SCENE.gltf";
        const cake = "cake/CakeModel.gltf";

        const parent = new THREE.Group();
        // const thethaDenominator = twoPI * (1 / denominator);

        // parent.add(new THREE.Mesh(new THREE.SphereBufferGeometry(1)));

        const fractions = geometries[geometryType];
        // const tray = new THREE.Mesh(tmpGeometry, new THREE.MeshBasicMaterial());
        let tray = null;

        if (fractions.length > 0) {
            fractions.forEach(fraction => { RenderManager.activeScene.add(fraction); });

            return fractions;
        }

        for (let i = 0; i < denominator; i++) {
            const object = new THREE.Mesh(tmpGeometry, new THREE.MeshBasicMaterial());

            if (geometryType === "cake") {
            } else if (geometryType === "pizza") {
            } else {
                const geometry = new THREE.CircleBufferGeometry(1, 64, 0, twoPI * (1 / (i + 1)));
                geometry.rotateX(Math.PI * 0.5);
                const material = MaterialBuilder.build({ side: THREE.DoubleSide, materialType: "pbr" });

                object.material = material;
                object.geometry = geometry;

                // geometries.basic.push(object);
            }

            fractions.push(object);

            object.denominator = i + 1;

            object.position.set(-2 * i + 3, 0, 0)//.applyAxisAngle(axis, thethaDenominator * i);

            parent.add(object);
        }
        tray = new Promise(resolve => {
            if (geometryType === "pizza") {
                const names = ["Pizza_1", "Pizza_12", "Pizza_13", "Pizza_14"];
                const scale = 4;
                gltfLoader.load(urlBase + pizzaPie, function (gltf) {
                    let el;

                    for (let i = 0; i < denominator; i++) {
                        el = gltf.scene.getObjectByName(names[i]);
                        const object = fractions[i];
                        object.geometry = el.geometry;
                        object.material = el.material;
                        object.material.envMap = AssetLibrary.envLight.activeMap;
                        el.geometry.scale(scale, scale, scale);

                        if (cards) {
                            for (let j = 1; j < 3; j++) {
                                const visual = new THREE.Mesh(object.geometry, object.material);
                                visual.position.set(-2 * i + 3, -j * 0.02, 0)
                                    .add(new THREE.Vector3(j * 0.1, 0, j * 0.1));

                                parent.add(visual);
                            }
                        }
                    }

                    el = gltf.scene.getObjectByName("Pizza_box");

                    const geometry = el.geometry;
                    const material = el.material;
                    material.envMap = AssetLibrary.envLight.activeMap;
                    el.geometry.scale(scale, scale, scale);
                    el.geometry.rotateY(Math.PI * 0.25);
                    // el.geometry.rotateX(Math.PI * -0.25);

                    resolve({ geometry, material });

                }, undefined, function (e) { console.error(e) });

            } else if (geometryType === "cake") {
                const names = ["CakeFull", "CakeHalf", "CakeThird", "CakeQuarter"];
                const scale = 4;
                gltfLoader.load(urlBase + cake, function (gltf) {
                    let el;

                    for (let i = 0; i < denominator; i++) {
                        el = gltf.scene.getObjectByName(names[i]);
                        const object = fractions[i];
                        object.geometry = el.geometry;
                        object.material = el.material;
                        object.material.envMap = AssetLibrary.envLight.activeMap;
                        el.geometry.scale(scale, scale, scale);

                        if (cards) {
                            for (let j = 1; j < 3; j++) {
                                const visual = new THREE.Mesh(object.geometry, object.material);
                                visual.position.set(-2 * i + 3, -j * 0.02, 0)
                                    .add(new THREE.Vector3(j * 0.1, 0, j * 0.1));

                                parent.add(visual);
                            }
                        }
                    }

                    el = gltf.scene.getObjectByName("Stand");

                    const geometry = el.geometry;
                    const material = el.material;
                    material.envMap = AssetLibrary.envLight.activeMap;
                    el.geometry.scale(scale, scale, scale);
                    el.geometry.rotateY(Math.PI * 0.25);
                    // el.geometry.rotateX(Math.PI * -0.25);

                    resolve({ geometry, material });

                }, undefined, function (e) { console.error(e) });
            } else {
                const geometry = new THREE.CircleBufferGeometry(1, 64, 0);
                geometry.rotateX(Math.PI * 0.5);

                resolve({ geometry, material: new THREE.MeshBasicMaterial() });
            }
        });

        RenderManager.activeScene.add(parent);

        return { objects: fractions, tray, parent };
    };
})();

function cleanupGeometries(objects) {
    objects.forEach(o => o.parent.remove(o));
}

function simplifyFraction(numerator, denominator) {
    function reduce(numerator, denominator) {
        let gcd = function gcd(a, b) { return b ? gcd(b, a % b) : a; };
        gcd = gcd(numerator, denominator);
        return { numerator: numerator / gcd, denominator: denominator / gcd };
    }
    return reduce(numerator, denominator)
}

export default AssessmentMath;
export { AssessmentMath, createDroppableObjects, cleanupGeometries, simplifyFraction };

const createDropzones = (function () {
    const quaternion = new THREE.Quaternion();
    // const tmp = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), Math.PI * -0.25);

    return function (slots, position) {
        const thethaSlots = twoPI * (1 / slots);
        const geometryPlop = new THREE.CircleBufferGeometry(1, 64, 0, thethaSlots);
        geometryPlop.rotateX(Math.PI * 0.5);

        const plops = [];

        for (let i = 0; i < slots; i++) {
            const thetha = thethaSlots * i;

            quaternion.setFromAxisAngle(axis, thetha);

            // quaternion.premultiply(tmp);
            // quaternion.set(0, 0, 0, 1);

            const matrix = new THREE.Matrix4().makeRotationFromQuaternion(quaternion);
            matrix.setPosition(position)

            plops.push({ matrix, thetha });
        }


        return { plops, geometryPlop };
    }
})();